// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Checkbox from '@dtci/spellbook/Checkbox';
import Input from '@dtci/spellbook/Input';
import Toggle from '@dtci/spellbook/Toggle';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Actions & Hooks
import { useRightsActions } from 'datastage/store/rights/useRights';
import { useRightsData } from '../../hooks/useRightsData';
// --- Utilities
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { cleanRightsFilter } from './utils/rightsSearchUtils';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
// --- Styles
import './styles.scss';

/**
 * RightsSearchForm
 *
 * Features:
 * - Renders a filter form for rights (ID, Title, Template, Deal ID, Catalog ID, Associations, Show Failed Associations)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for ID fields
 * - Associations filter uses checkboxes (Deal and Catalog), both checked by default
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function RightsSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const { fetchRights, updateRights, rightRange, rightSort, itemsPerPage } = useRightsData();
    const { updateRightsForm, clearRights } = useRightsActions();
    const { filterForm } = useSelector((s) => s.rights);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        rightId: undefined,
        dealId: undefined,
        catalogId: undefined,
    });

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateRights({
                    itemsPerPage: newPageSize,
                    rightRange: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateRightsForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    /**
     * Builds filter form data from URL search params.
     * Converts string params to appropriate types.
     */
    function buildFilterFromParams(params) {
        const filterData = {};

        if (params.rightId) filterData.rightId = params.rightId;
        if (params.rightTitle) filterData.rightTitle = params.rightTitle;
        if (params.rightTemplate) filterData.rightTemplate = params.rightTemplate;
        if (params.dealId) filterData.dealId = params.dealId;
        if (params.catalogId) filterData.catalogId = params.catalogId;

        // Convert showFailedAssociations to boolean
        if ('showFailedAssociations' in params) {
            filterData.showFailedAssociations =
                params.showFailedAssociations === 'true' || params.showFailedAssociations === true;
        }

        // Convert hasDeal and hasCatalog to boolean (default to true if not specified)
        if ('hasDeal' in params) {
            filterData.hasDeal = params.hasDeal === 'true' || params.hasDeal === true;
        }
        if ('hasCatalog' in params) {
            filterData.hasCatalog = params.hasCatalog === 'true' || params.hasCatalog === true;
        }

        return filterData;
    }

    /**
     * Fetches rights data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchRights(range, filterData);
        if (data) {
            updateRights({
                rights: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                rightRange: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearRights();
        setValidationErrors({
            rightId: undefined,
            dealId: undefined,
            catalogId: undefined,
        });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates ID fields for special characters.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate ID fields for special characters
        if (['rightId', 'dealId', 'catalogId'].includes(field)) {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateRightsForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateRights({ rightRange: firstPageRange, currentPage: 0 });
                const filtered = cleanRightsFilter(newFilterForm, firstPageRange, rightSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="rights-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Right ID">
                    <Input
                        value={filterForm.rightId}
                        onChange={(e) => handleInputChange('rightId', e.target.value)}
                        width="auto"
                        id="rightId"
                        name="rightId"
                        placeholder="Enter"
                        className={validationErrors.rightId ? 'input-error' : ''}
                    />
                    {validationErrors.rightId && (
                        <div className="validation-error">{validationErrors.rightId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Title">
                    <Input
                        value={filterForm.rightTitle}
                        onChange={(e) => handleInputChange('rightTitle', e.target.value)}
                        width="auto"
                        id="rightTitle"
                        name="rightTitle"
                        placeholder="Enter"
                        isDisabled={!!filterForm.showFailedAssociations === true}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Template">
                    <Input
                        value={filterForm.rightTemplate}
                        onChange={(e) => handleInputChange('rightTemplate', e.target.value)}
                        width="auto"
                        id="rightTemplate"
                        name="rightTemplate"
                        placeholder="Enter"
                        isDisabled={!!filterForm.showFailedAssociations === true}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Deal ID">
                    <Input
                        value={filterForm.dealId}
                        onChange={(e) => handleInputChange('dealId', e.target.value)}
                        width="auto"
                        id="dealId"
                        name="dealId"
                        placeholder="Enter"
                        isDisabled={!!filterForm.showFailedAssociations === true}
                        className={validationErrors.dealId ? 'input-error' : ''}
                    />
                    {validationErrors.dealId && (
                        <div className="validation-error">{validationErrors.dealId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Catalog ID">
                    <Input
                        value={filterForm.catalogId}
                        onChange={(e) => handleInputChange('catalogId', e.target.value)}
                        width="auto"
                        id="catalogId"
                        name="catalogId"
                        placeholder="Enter"
                        isDisabled={!!filterForm.showFailedAssociations === true}
                        className={validationErrors.catalogId ? 'input-error' : ''}
                    />
                    {validationErrors.catalogId && (
                        <div className="validation-error">{validationErrors.catalogId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Associations">
                    <div className="associations-checkboxes">
                        <Checkbox
                            option={{ id: 'hasDeal', name: 'hasDeal', label: 'Deal' }}
                            checked={filterForm.hasDeal}
                            onChange={() => handleInputChange('hasDeal', !filterForm.hasDeal)}
                            disabled={!!filterForm.showFailedAssociations}
                        />
                        <Checkbox
                            option={{ id: 'hasCatalog', name: 'hasCatalog', label: 'Catalog' }}
                            checked={filterForm.hasCatalog}
                            onChange={() => handleInputChange('hasCatalog', !filterForm.hasCatalog)}
                            disabled={!!filterForm.showFailedAssociations}
                        />
                    </div>
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Show Failed Associations">
                    <Toggle
                        checked={!!filterForm.showFailedAssociations}
                        onChange={(e) => handleInputChange('showFailedAssociations', e.target.checked)}
                        labelStyle={'yesNo'}
                    />
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
